/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
/* eslint-disable no-nested-ternary */
import PropTypes from "prop-types";
import {
  Button,
  DatePicker,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload,
  Input,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
import Papa from "papaparse";

import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import { WL_PLAN_TIERS_TITLE, WL_PLAN_TITLE } from "../../Utils/constants";
import { showAppError } from "../../Utils/error";
import { numFormatter } from "../../Utils/formatter";
import { bulkCreateManualWalletDebit, getCreatorPayables } from "../api";
import ManualDebitHandler from "./ManualDebitHandler";

const { Option } = Select;
const { TextArea } = Input;

const INPUT_WIDTH = "100%";

const intialFilters = {
  search: "",
  startDate: moment(new Date("2022-03-01T16:32:32.956+00:00")),
  endDate: moment().endOf("day"),
  whitelabelPlanType: null,
  userTier: null,
  creatorCountryType: null,
};

const CreatorPayables = ({ sensitiveAllowedActions }) => {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  // Hard code the start date to 9th March 2024 when the
  // ledger system started, why hard code? because i expect a future
  // requirement where we would want this to be variable.
  // why this date? because we migrated the order dates and not the actual
  // creation date.
  // const startDate = moment(new Date("2022-03-01T16:32:32.956+00:00"));
  const [filters, setFilters] = useState(intialFilters);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  const [isUploadCSVModalOpen, setIsUploadCSVModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [invalidCsvData, setInvalidCsvData] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [fileList, setFileList] = useState([]);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("manual_debit");
  const sensitiveAllowedFunctionSet = React.useMemo(
    () => new Set(sensitiveAllowedActions),
    [sensitiveAllowedActions]
  );

  const isManualWalletDebitAllowed =
    sensitiveAllowedFunctionSet.has("manualWalletDebit");

  const onChangeFilters = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const fetchCreatorPayables = async () => {
    setLoading(true);
    try {
      const resp = await getCreatorPayables(
        filters.startDate.toISOString(),
        filters.endDate.toISOString(),
        filters.search,
        filters.whitelabelPlanType,
        filters.userTier,
        filters.creatorCountryType,
        page,
        limit
      );
      if (resp?.code === 0) {
        setData(resp?.result?.data);
        setTotal(resp?.result?.total);
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setLoading(false);
    }
  };

  const reloadCreatorPayables = () => {
    setData([]);
    fetchCreatorPayables();
  };

  const handleCreatorPayablesCSVDownload = async () => {
    setCsvLoading(true);
    try {
      const resp = await getCreatorPayables(
        filters.startDate.toISOString(),
        filters.endDate.toISOString(),
        filters.search,
        filters.whitelabelPlanType,
        filters.userTier,
        filters.creatorCountryType,
        page,
        limit,
        true
      );
      fileDownload(resp, "creator-payables.csv");
    } catch (error) {
      showAppError(error);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleMigrateManualDebit = async () => {
    try {
      const resp = await bulkCreateManualWalletDebit({
        users: csvData.map((user) => ({
          creatorId: user.creator_id,
          amount: user.amount,
          currency: user.currency,
          name: user.name || "",
          description: description || "Manual debit",
          category,
        })),
      });
      if (resp?.code === 0) {
        const result = resp?.result;

        // Check if there are validation errors
        if (result?.validationErrors && result.validationErrors.length > 0) {
          // Create CSV data for validation errors
          const errorCsvData = result.validationErrors.map((error) => ({
            row: error.row,
            creatorId: error.data?.creatorId || "N/A",
            currency: error.data?.currency || "N/A",
            amount: error.data?.amount || "N/A",
            name: error.data?.name || "N/A",
            validationErrors: error.validationErrors.join("; "),
          }));

          // Convert to CSV and download
          const csv = Papa.unparse(errorCsvData, {
            header: true,
            columns: [
              "row",
              "creatorId",
              "currency",
              "amount",
              "name",
              "validationErrors",
            ],
          });

          // Create filename using original uploaded file name
          const originalName = uploadedFileName.replace(/\.csv$/i, "");
          const errorFileName = `${originalName}-validation-errors-${moment().format(
            "YYYY-MM-DD-HH-mm-ss"
          )}.csv`;

          fileDownload(csv, errorFileName);

          message.warning(
            `Migration completed with ${result.summary.invalid} validation errors. Error CSV downloaded.`
          );
        } else {
          message.success(
            `Manual debit migrated successfully! ${result.summary.successful} records processed.`
          );
        }

        // Close modal and reset data after processing
        setIsUploadCSVModalOpen(false);
        setCsvData([]);
        setInvalidCsvData([]);
        setUploadedFileName("");
        setDescription("");

        // Show summary if available
        if (result?.summary) {
          const {
            total: summaryTotal,
            valid,
            invalid,
            processed,
            successful,
            failed,
          } = result.summary;
          console.log("Migration Summary:", {
            total: summaryTotal,
            valid,
            invalid,
            processed,
            successful,
            failed,
          });
        }
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const handleCsvUpload = (file) => {
    setUploadedFileName(file.name);

    const headersMustPresent = ["creator_id", "amount", "currency"];
    const allowedCurrencies = ["INR", "USD", "EUR"];
    Papa.parse(file, {
      complete: (results) => {
        const { data: parsedData, meta } = results;
        setInvalidCsvData([]);

        const headers = meta.fields;
        const headersPresent = headersMustPresent.every((header) =>
          headers.includes(header)
        );
        if (!headersPresent) {
          message.error(
            `Invalid CSV file. Headers must present: ${headersMustPresent.join(
              ", "
            )}. Please check the headers and try again.`
          );
          return false;
        }

        const invalidData = [];
        const correctData = [];
        // now checking for validation
        parsedData.forEach((row, index) => {
          let hasError = false;
          const { creator_id, amount, currency } = row;
          if ((!creator_id || !amount || !currency) && !hasError) {
            invalidData.push({
              row: index + 1,
              message: "Creator ID, amount, and currency are required",
            });
            hasError = true;
          }
          const amountNumber = Number(amount?.replace(/,/g, ""));
          console.log("amount", amount, "amountNumber", amountNumber);
          if (Number.isNaN(amountNumber) && !hasError) {
            invalidData.push({
              row: index + 1,
              message: `Amount is not a number, ${amount}`,
            });
            hasError = true;
          }

          if (creator_id === "" && !hasError) {
            invalidData.push({
              row: index + 1,
              message: "Creator ID is empty",
            });
            hasError = true;
          }

          if (
            currency &&
            !allowedCurrencies.includes(currency.toUpperCase()) &&
            !hasError
          ) {
            invalidData.push({
              row: index + 1,
              message: `Invalid currency: ${currency}. Allowed values are: ${allowedCurrencies.join(
                ", "
              )}`,
            });
            hasError = true;
          }

          if (!hasError) {
            correctData.push({
              creator_id: row.creator_id,
              amount: amountNumber,
              currency: currency?.toUpperCase(),
              name: row.name,
            });
          }
        });

        if (invalidData.length > 0) {
          setInvalidCsvData(invalidData);
        } else {
          setCsvData(correctData);
        }
      },
      header: true,
    });
    return false;
  };

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      reloadCreatorPayables();
    }
  }, [limit, filters]);

  useEffect(() => {
    reloadCreatorPayables();
  }, [page]);

  const columns = [
    {
      title: "Creator",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <>
          <TableUserInfoItem
            name={record?.name}
            profilePic={record?.profilePicUrl}
            email={record?.email}
            phone={record?.phone}
            country={record?.country}
            dialCode={record?.dialCode}
            isCopyable
          />
          <Typography.Title
            level={5}
            style={{ fontSize: 14, fontWeight: 500, marginTop: 8 }}
          >
            {WL_PLAN_TITLE?.[record?.whitelabelPlanType] || "N/A"},{" "}
            {WL_PLAN_TIERS_TITLE?.[record?.userTier] || "N/A"}
          </Typography.Title>
        </>
      ),
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (host) =>
        host ? <Typography.Link href={host}>{host}</Typography.Link> : "-",
    },
    {
      title: "Account Type",
      dataIndex: "accountType",
      key: "accountType",
      render: (accountType) =>
        accountType ? (
          <Typography.Text>
            {accountType === "india" ? "INDIAN" : accountType?.toUpperCase()}
          </Typography.Text>
        ) : (
          "-"
        ),
    },
    {
      title: "Total Payables",
      dataIndex: "totalPayables",
      key: "totalPayables",
      render: (amount) => numFormatter({ amount }),
    },
    {
      title: "Total Withdrawn",
      dataIndex: "totalWithdrawn",
      key: "totalWithdrawn",
      render: (amount) => numFormatter({ amount }),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <ManualDebitHandler
            creatorId={record?.creatorId}
            creatorName={record?.name}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Creator Payables
      </Typography.Title>

      <FiltersWrapper style={{ marginBottom: 12 }}>
        <CustomSearchbar
          placeholder="Search by creator name, email, phone or host"
          searchQuery={filters.search}
          setSearchQuery={(value) => onChangeFilters("search", value)}
          style={{ marginBottom: 0, maxWidth: 500 }}
        />

        <Tooltip title="Refresh">
          <Button size="large" onClick={reloadCreatorPayables}>
            <ReloadOutlined />
          </Button>
        </Tooltip>

        {/* <Upload accept=".csv" beforeUpload={handleCsvUpload}>
          <Button>Upload CSV</Button>
        </Upload> */}
        {isManualWalletDebitAllowed ? (
          <Button
            className="siteBtn grayBtn"
            style={{ marginLeft: "auto" }}
            onClick={() => {
              setIsUploadCSVModalOpen(true);
              setCsvData([]);
              setInvalidCsvData([]);
              setUploadedFileName("");
              setDescription("");
            }}
          >
            Migrate manual debit
          </Button>
        ) : null}

        <Button
          className="siteBtn grayBtn"
          onClick={handleCreatorPayablesCSVDownload}
          loading={csvLoading}
          disabled={csvLoading}
          style={{ marginLeft: !isManualWalletDebitAllowed ? "auto" : 0 }}
        >
          <DownloadOutlined />
          Export CSV
        </Button>
      </FiltersWrapper>

      <FiltersWrapper style={{ flexWrap: "nowrap" }}>
        <DatePicker
          size="large"
          value={filters.endDate}
          onChange={(date) =>
            onChangeFilters("endDate", moment(date).endOf("day"))
          }
          allowClear={false}
          style={{ width: INPUT_WIDTH }}
        />

        <Select
          size="large"
          allowClear
          placeholder="Select plan"
          value={filters.whitelabelPlanType}
          onSelect={(value) => onChangeFilters("whitelabelPlanType", value)}
          onClear={() => onChangeFilters("whitelabelPlanType", null)}
          style={{ width: INPUT_WIDTH }}
        >
          {Object.entries(WL_PLAN_TITLE).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>

        <Select
          size="large"
          allowClear
          placeholder="Select tier"
          value={filters.userTier}
          onSelect={(value) => onChangeFilters("userTier", value)}
          onClear={() => onChangeFilters("userTier", null)}
          style={{ width: INPUT_WIDTH }}
        >
          {Object.entries(WL_PLAN_TIERS_TITLE).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>

        <Select
          size="large"
          allowClear
          placeholder="Select account type"
          value={filters.creatorCountryType}
          onSelect={(value) => onChangeFilters("creatorCountryType", value)}
          onClear={() => onChangeFilters("creatorCountryType", null)}
          style={{ width: INPUT_WIDTH }}
        >
          <Option value="india">INDIAN</Option>
          <Option value="international">INTERNATIONAL</Option>
          <Option value="offshore">OFFSHORE</Option>
        </Select>
      </FiltersWrapper>

      <Table
        columns={columns}
        dataSource={data}
        pagination={{
          pageSize: limit,
          total,
          onChange: setPage,
          current: page,
          onShowSizeChange: (_, size) => setLimit(size),
        }}
        loading={loading}
        scroll={{ x: "auto" }}
        locale={{
          emptyText: loading ? "Loading..." : "No creator payables found",
        }}
      />

      <Modal
        open={isUploadCSVModalOpen}
        onCancel={() => {
          setIsUploadCSVModalOpen(false);
          setCsvData([]);
          setInvalidCsvData([]);
          setUploadedFileName("");
          setDescription("");
        }}
        footer={null}
        width={600}
      >
        <Typography.Title level={4}>Migrate manual debit</Typography.Title>

        <div style={{ marginBottom: 16 }}>
          <Typography.Text>
            Upload a CSV file with columns(mandatory):{" "}
            <strong>creator_id</strong>, <strong>amount</strong>,{" "}
            <strong>currency</strong>
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Allowed currencies: INR, USD, EUR
          </Typography.Text>
        </div>

        <Select
          placeholder="Select category"
          value={category}
          onChange={(value) => setCategory(value)}
        >
          <Option value="manual_debit_tds">TDS</Option>
          <Option value="manual_debit_gst">GST</Option>
          <Option value="manual_debit_tcs">TCS</Option>
          <Option value="manual_debit_disputes">Disputes</Option>
          <Option value="manual_debit">Manual Debit</Option>
        </Select>

        <Upload
          accept=".csv"
          beforeUpload={handleCsvUpload}
          fileList={fileList} // Add this prop
          onRemove={() => {
            setFileList([]);
            setCsvData([]);
            setInvalidCsvData([]);
            setUploadedFileName("");
            setDescription("");
          }}
        >
          <Button>Upload CSV</Button>
        </Upload>

        <div style={{ marginTop: 16 }}>
          <Typography.Text strong>Description:</Typography.Text>
          <TextArea
            placeholder="Enter description for this manual debit migration"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ marginTop: 8 }}
          />
        </div>

        {invalidCsvData.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Typography.Title
              level={5}
              style={{ color: "#ff4d4f", marginBottom: 8 }}
            >
              Validation Errors Found:
            </Typography.Title>
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #ff4d4f",
                borderRadius: 4,
                padding: 12,
                backgroundColor: "#fff2f0",
              }}
            >
              {invalidCsvData.map((error) => (
                <div key={`error-${error.row}`} style={{ marginBottom: 8 }}>
                  <Typography.Text style={{ color: "#ff4d4f" }}>
                    <strong>Row {error.row}:</strong> {error.message}
                  </Typography.Text>
                </div>
              ))}
            </div>
            <Typography.Text
              style={{ color: "#ff4d4f", fontSize: 12, marginTop: 8 }}
            >
              Please fix these errors and upload the CSV again.
            </Typography.Text>
          </div>
        )}

        {csvData.length > 0 && invalidCsvData.length === 0 && (
          <div style={{ marginTop: 16 }}>
            <Typography.Title
              level={5}
              style={{ color: "#52c41a", marginBottom: 8 }}
            >
              CSV Validated Successfully!
            </Typography.Title>
            <div
              style={{
                border: "1px solid #52c41a",
                borderRadius: 4,
                padding: 12,
                backgroundColor: "#f6ffed",
                marginBottom: 12,
              }}
            >
              <Typography.Text style={{ color: "#52c41a" }}>
                Found {csvData.length} valid records ready for processing.
              </Typography.Text>
            </div>

            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              Data Preview:
            </Typography.Title>
            <div
              style={{
                maxHeight: 300,
                overflowY: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: 4,
              }}
            >
              <Table
                dataSource={csvData}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Creator ID",
                    dataIndex: "creator_id",
                    key: "creator_id",
                    width: "33%",
                  },
                  // {
                  //   title: "Creator Name",
                  //   dataIndex: "name",
                  //   key: "name",
                  //   width: "40%",
                  // },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    width: "30%",
                    render: (amount) => `${amount}`,
                  },
                  {
                    title: "Currency",
                    dataIndex: "currency",
                    key: "currency",
                    width: "30%",
                  },
                ]}
                rowKey={(record, index) =>
                  `preview-${record.creator_id}-${index}`
                }
              />
            </div>
            <Button
              style={{
                marginLeft: "auto",
                marginTop: "16px",
              }}
              type="primary"
              onClick={handleMigrateManualDebit}
              disabled={!description.trim()}
            >
              Migrate
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};
CreatorPayables.propTypes = {
  sensitiveAllowedActions: PropTypes.shape([]),
};

export default CreatorPayables;
